import React from 'react';

import { ctw } from '@/common';
import { WarningFilledSvg } from '@/components/atoms';
import { Check } from '../../../../common/icons/lucide-adapter';
import { ReasoningTooltip } from '../../primitives';
import { riskTypeMapper } from '../constants';

type RiskTypesDisplayProps = {
  riskTypes:
    | Record<string, { status: 'clear' | 'alert'; reasoning?: string | null } | null>
    | null
    | undefined;
  allowedKeys?: Array<keyof typeof riskTypeMapper>;
};

export const RiskTypesDisplay: React.FC<RiskTypesDisplayProps> = ({ riskTypes, allowedKeys }) => {
  if (!riskTypes) {
    return null;
  }

  const keysToShow =
    allowedKeys ?? (Object.keys(riskTypeMapper) as Array<keyof typeof riskTypeMapper>);

  return (
    <div className="space-y-2">
      {keysToShow.map(name => {
        if (!riskTypes[name]) {
          return null;
        }

        const { status, reasoning } = riskTypes[name];
        const { Icon, title } = riskTypeMapper[name];

        // The row's own content — status indicator + risk-type icon + title. Reused verbatim
        // as the tooltip heading so the tooltip names its subject exactly as the report
        // header renders it, rather than inventing a separate pill treatment.
        const label = (
          <>
            {status === 'clear' ? (
              <div className="flex h-4 w-4 items-center justify-center rounded-full bg-stone-500/10">
                <Check className="h-2 w-2 text-stone-400" strokeWidth={6} />
              </div>
            ) : (
              <div className="flex h-5 w-5 items-center justify-center">
                <WarningFilledSvg className="h-6 w-6" />
              </div>
            )}

            <div
              className={ctw(
                'flex items-center gap-2 text-sm',
                status === 'clear' ? 'text-stone-400' : 'text-slate-900',
              )}
            >
              <Icon className="h-4 w-4" strokeWidth={1} />
              <span>{title}</span>
            </div>
          </>
        );

        return (
          <ReasoningTooltip
            key={name}
            reasoning={reasoning}
            title={<div className="flex items-center gap-2">{label}</div>}
          >
            <div
              className={ctw('flex items-center gap-2', reasoning?.trim() && 'cursor-help')}
              data-testid="risk-type"
              // BAL-8159 a11y: focusable only when it has reasoning, so keyboard/SR users
              // can open the tooltip (radix shows it on focus); cleared rows stay out of tab order.
              tabIndex={reasoning?.trim() ? 0 : undefined}
            >
              {label}
            </div>
          </ReasoningTooltip>
        );
      })}
    </div>
  );
};
